import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Paper from "@mui/material/Paper";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import UndoIcon from "@mui/icons-material/Undo";
import { styled } from "@mui/material/styles";
import { GameHeader } from "../../WordyVerse/Components/GameBoard/GameHeader";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import Tooltip from "@mui/material/Tooltip";
import * as math from "mathjs";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StreakCounter from "../../../components/StreakCounter";
import Chip from "@mui/material/Chip";
import TimerIcon from "@mui/icons-material/Timer";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Howl } from "howler";
import { formatDateForStorage } from "../../../components/GameStateUtil";
import CalendarArchive from "../components/CalendarArchive";
import HowToPlayModal from "../components/HowToPlayModal";
import {
  getPonderStreakFromStorage,
  updatePonderStreak,
  getIsPonderStreakActive,
} from "../components/PonderStreakUtil";
import EndGameModal from "../components/EndGameModal";

// Styled components
const GameContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(1),
  backgroundColor: "#F5F0EB",
}));

const GameCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 600,
  margin: "0 auto",
  backgroundColor: "#F5F0EB",
  boxShadow: "none",
}));

const GameGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(4),
  aspectRatio: "1 / 1",
  maxWidth: "sm",
  margin: "0 auto",
}));

const Controls = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(3),
  margin: theme.spacing(4, 0),
  padding: theme.spacing(2),
}));

const ControlButton = styled(IconButton)(({ theme }) => ({
  width: 56,
  height: 56,
  backgroundColor: "#FFFFFF",
  border: "2px solid #000000",
  boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.2)",
  padding: theme.spacing(2),
  "&:hover": {
    backgroundColor: "#F0F0F0",
    boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.2)",
  },
  "&.Mui-disabled": {
    backgroundColor: "#F0F0F0",
    border: "2px solid rgba(0, 0, 0, 0.2)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.75rem",
    color: "#000000",
  },
}));

const TargetNumber = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#F0F0F0",
  border: "2px solid #000000",
  borderRadius: theme.shape.borderRadius,
  fontWeight: "bold",
  fontSize: "1.2rem",
  color: "#000000",
  boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.2)",
}));

const GameTile = styled(Button)(({ theme, isTarget, isCorrect, isEmpty }) => ({
  aspectRatio: "1 / 1",
  fontSize: "1.5rem",
  fontWeight: "bold",
  backgroundColor: isEmpty
    ? "#F0F0F0"
    : isCorrect
    ? "#abc9eb" // Light green background for correct numbers
    : "#FFFFFF", // White background for numbers not in place
  color: "#000000",
  border: isEmpty ? "2px dashed #000000" : "2px solid #000000",
  boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.2)",
  borderRadius: "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: isEmpty ? "#F0F0F0" : isCorrect ? "#E8F5E9" : "#F0F0F0",
    boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.2)",
    transform: "translateY(2px)",
  },
  "&.Mui-disabled": {
    backgroundColor: isEmpty ? "#F0F0F0" : isCorrect ? "#E8F5E9" : "#FFFFFF",
    color: isEmpty ? "transparent" : "#000000",
    border: isEmpty ? "2px dashed #000000" : "2px solid #000000",
    opacity: 0.8,
  },
}));

const DigitShift = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [board, setBoard] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [isSolved, setIsSolved] = useState(false);
  const [moveHistory, setMoveHistory] = useState([]);
  const [correctTiles, setCorrectTiles] = useState(new Set());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [targetNumbers, setTargetNumbers] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [hintsRemaining, setHintsRemaining] = useState(3);
  const [hintHistory, setHintHistory] = useState([]);
  const [hintLevel, setHintLevel] = useState(0);
  const [hintNumber, setHintNumber] = useState(null);
  const [hintPosition, setHintPosition] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [bestTime, setBestTime] = useState(null);
  const [newBestTime, setNewBestTime] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [streak, setStreak] = useState(0);
  const [isStreakActive, setIsStreakActive] = useState(false);
  const hasInitialized = useRef(true);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showHowToModal, setShowHowToModal] = useState(false);

  // Add sound instances
  const clickSound = useRef(
    new Howl({
      src: ["/assets/audio/click.ogg", "/assets/audio/click.mp3"],
      volume: 0.5,
    })
  );
  const errorSound = useRef(
    new Howl({
      src: ["/assets/audio/error.ogg", "/assets/audio/error.mp3"],
      volume: 0.2,
    })
  );
  const winSound = useRef(
    new Howl({
      src: ["/assets/audio/level-win.mp3"],
      volume: 0.3,
    })
  );
  const buttonClickSound = useRef(
    new Howl({
      src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
      volume: 0.2,
    })
  );

  // Add effect to handle mute state
  useEffect(() => {
    clickSound.current.mute(isMuted);
    errorSound.current.mute(isMuted);
    winSound.current.mute(isMuted);
    buttonClickSound.current.mute(isMuted);
  }, [isMuted]);

  // Local seeded random function (LCG)
  function seededRandomLCG(seed) {
    let state = seed;
    return function () {
      state = (state * 1664525 + 1013904223) % 4294967296;
      return state / 4294967296;
    };
  }

  const makeRandomMove = (currentBoard, emptyPos, lastMove = null) => {
    const possibleMoves = [];

    // Check all possible moves (up, down, left, right)
    if (emptyPos >= 4) possibleMoves.push(emptyPos - 4); // up
    if (emptyPos < 12) possibleMoves.push(emptyPos + 4); // down
    if (emptyPos % 4 !== 0) possibleMoves.push(emptyPos - 1); // left
    if (emptyPos % 4 !== 3) possibleMoves.push(emptyPos + 1); // right

    // Remove the reverse of the last move to avoid undoing moves
    if (lastMove !== null) {
      const reverseIndex = possibleMoves.indexOf(lastMove);
      if (reverseIndex !== -1) {
        possibleMoves.splice(reverseIndex, 1);
      }
    }

    // Return null if no moves are possible
    if (possibleMoves.length === 0) return null;

    // Pick a random move
    const moveIndex = Math.floor(Math.random() * possibleMoves.length);
    const nextPos = possibleMoves[moveIndex];

    // Make the move
    const newBoard = [...currentBoard];
    [newBoard[emptyPos], newBoard[nextPos]] = [
      newBoard[nextPos],
      newBoard[emptyPos],
    ];

    return { board: newBoard, emptyPos: nextPos, lastMove: emptyPos };
  };

  const handleDateSelect = (date) => {
    console.log("DigitShift: handleDateSelect called with date:", date);
    setCurrentDate(date);
    setShowCalendar(false);
    // Reset game state
    console.log("DigitShift: Resetting game state...");
    setBoard([]);
    setElapsedTime(0);
    setIsActive(false);
    setGameOver(false);
    setIsSolved(false);
    setMoveHistory([]);
    setCorrectTiles(new Set());
    setHasStarted(false);
    setHintsRemaining(3);
    setHintHistory([]);
    setHintLevel(0);
    setHintNumber(null);
    setHintPosition(null);
    // Initialize new board
    console.log("DigitShift: Initializing new board...");
    initializeBoard();
  };

  // Add effect to handle date changes
  useEffect(() => {
    console.log("DigitShift: Date changed to:", currentDate);
    if (!hasInitialized.current) {
      console.log("DigitShift: Initializing board due to date change");
      initializeBoard();
      hasInitialized.current = true;
    }
  }, [currentDate]);

  const initializeBoard = () => {
    console.log("DigitShift: initializeBoard called");
    // Start with solved state
    let numbers = Array.from({ length: 15 }, (_, i) => i + 1);
    numbers.push(null); // Add empty space at the end

    // Get today's date as seed (YYYYMMDD in UTC)
    const year = currentDate.getUTCFullYear();
    const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getUTCDate()).padStart(2, "0");
    const seed = parseInt(`${year}${month}${day}`, 10);
    console.log(
      "DigitShift: Using UTC date for seed:",
      `${year}-${month}-${day}`,
      "seed:",
      seed
    );

    // Create a local seeded random function
    const rand = seededRandomLCG(seed);

    // Make exactly 20 random moves using the seeded random
    let currentBoard = numbers;
    let emptyPos = 15; // Start with empty tile at bottom right
    let lastMove = null;

    for (let i = 0; i < 20; i++) {
      // Use the seeded random for move selection
      const possibleMoves = [];
      if (emptyPos >= 4) possibleMoves.push(emptyPos - 4); // up
      if (emptyPos < 12) possibleMoves.push(emptyPos + 4); // down
      if (emptyPos % 4 !== 0) possibleMoves.push(emptyPos - 1); // left
      if (emptyPos % 4 !== 3) possibleMoves.push(emptyPos + 1); // right
      // Remove the reverse of the last move to avoid undoing moves
      if (lastMove !== null) {
        const reverseIndex = possibleMoves.indexOf(lastMove);
        if (reverseIndex !== -1) {
          possibleMoves.splice(reverseIndex, 1);
        }
      }
      if (possibleMoves.length === 0) break;
      const moveIndex = Math.floor(rand() * possibleMoves.length);
      const nextPos = possibleMoves[moveIndex];
      // Make the move
      const newBoard = [...currentBoard];
      [newBoard[emptyPos], newBoard[nextPos]] = [
        newBoard[nextPos],
        newBoard[emptyPos],
      ];
      currentBoard = newBoard;
      lastMove = emptyPos;
      emptyPos = nextPos;
    }

    console.log("DigitShift: Setting new board:", currentBoard);
    setBoard(currentBoard);
    updateCorrectTiles(currentBoard);
  };

  // Add effect to load streak when date changes
  useEffect(() => {
    const { count } = getPonderStreakFromStorage("digitshift");
    setStreak(count);
    setIsStreakActive(getIsPonderStreakActive("digitshift"));
  }, [currentDate]);

  useEffect(() => {
    if (hasStarted && isBoardSolved()) {
      setIsSolved(true);
      setGameOver(true);
      setIsActive(false);
      winSound.current.play();

      // Update streak
      const newStreak = updatePonderStreak(new Date(), "digitshift");
      setStreak(newStreak);
      setIsStreakActive(true);

      // Store game completion data
      const dateKey = formatDateForStorage(currentDate);
      const gameData = {
        completed: true,
        time: elapsedTime,
        bestTime: bestTime !== null ? bestTime : elapsedTime,
      };
      localStorage.setItem(`digitshift-${dateKey}`, JSON.stringify(gameData));

      // Check for new best time
      if (!bestTime || elapsedTime < bestTime) {
        setNewBestTime(true);
        localStorage.setItem(
          `digitshift_bestTime-${dateKey}`,
          elapsedTime.toString()
        );
      } else {
        setNewBestTime(false);
      }

      // Show end modal
      setShowEndModal(true);
    }
  }, [board, hasStarted, elapsedTime, currentDate, bestTime]);

  // Load best time for the current date
  useEffect(() => {
    const dateKey = formatDateForStorage(currentDate);
    const best = localStorage.getItem(`digitshift_bestTime-${dateKey}`);
    setBestTime(best ? parseInt(best, 10) : null);
  }, [currentDate]);

  // Timer effect
  useEffect(() => {
    let intervalId;
    if (hasStarted && !gameOver) {
      intervalId = setInterval(() => {
        setElapsedTime((time) => time + 1);
      }, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [hasStarted, gameOver]);

  const isBoardSolved = () => {
    // Check if all numbers are in their correct positions
    return board.every((num, index) => {
      if (num === null) return true; // Empty space is always correct
      return num === index + 1; // This is correct - position 0 should have 1, position 1 should have 2, etc.
    });
  };

  const updateCorrectTiles = (currentBoard) => {
    const correct = new Set();
    currentBoard.forEach((num, index) => {
      if (num === index + 1) {
        // This is correct - same logic as above
        correct.add(index);
      }
    });
    setCorrectTiles(correct);
  };

  const handleTileClick = (index) => {
    if (gameOver) return;

    // Start game on first move
    if (!hasStarted) {
      setHasStarted(true);
      setElapsedTime(0);
    }

    const emptyIndex = board.indexOf(null);
    if (canMove(index, emptyIndex)) {
      clickSound.current.play();
      const newBoard = [...board];
      [newBoard[index], newBoard[emptyIndex]] = [
        newBoard[emptyIndex],
        newBoard[index],
      ];
      setBoard(newBoard);
      updateCorrectTiles(newBoard);
    } else {
      errorSound.current.play();
    }
  };

  const canMove = (index, emptyIndex) => {
    const row = math.floor(index / 4);
    const col = index % 4;
    const emptyRow = math.floor(emptyIndex / 4);
    const emptyCol = emptyIndex % 4;

    return (
      (math.abs(row - emptyRow) === 1 && col === emptyCol) ||
      (math.abs(col - emptyCol) === 1 && row === emptyRow)
    );
  };

  const handleUndo = () => {
    if (moveHistory.length > 0) {
      buttonClickSound.current.play();
      const lastMove = moveHistory[moveHistory.length - 1];
      const newBoard = [...board];
      [newBoard[lastMove.from], newBoard[lastMove.to]] = [
        newBoard[lastMove.to],
        newBoard[lastMove.from],
      ];
      setBoard(newBoard);
      setMoveHistory(moveHistory.slice(0, -1));
      updateCorrectTiles(newBoard);
    }
  };

  const getClosestNumberToPosition = () => {
    let closestNumber = null;
    let minDistance = Infinity;

    board.forEach((num, index) => {
      if (num === null) return;
      if (num === index + 1) return; // Skip numbers that are already in correct position
      const correctIndex = num - 1; // This is correct - number 2 should go to index 1
      const distance = math.abs(index - correctIndex);
      if (distance > 0 && distance < minDistance) {
        // Only consider numbers that aren't in position
        minDistance = distance;
        closestNumber = { num, currentIndex: index, correctIndex };
      }
    });

    return closestNumber;
  };

  const getNextHintNumber = () => {
    // First, try to find numbers in the bottom row that aren't in position
    for (let row = 3; row >= 0; row--) {
      for (let col = 0; col < 4; col++) {
        const index = row * 4 + col;
        const num = board[index];
        if (num === null) continue;

        // If this number belongs in this row but isn't in position
        const correctRow = math.floor((num - 1) / 4);
        if (correctRow === row && num !== index + 1) {
          return { num, currentIndex: index, correctIndex: num - 1 };
        }
      }

      // After checking for numbers that belong in this row,
      // look for any numbers that don't belong in this row
      for (let col = 0; col < 4; col++) {
        const index = row * 4 + col;
        const num = board[index];
        if (num === null) continue;

        const correctRow = math.floor((num - 1) / 4);
        if (correctRow !== row) {
          return { num, currentIndex: index, correctIndex: num - 1 };
        }
      }
    }

    // If no priority moves found, fall back to closest number
    let closestNumber = null;
    let minDistance = Infinity;

    board.forEach((num, index) => {
      if (num === null) return;
      if (num === index + 1) return; // Skip numbers that are already in correct position
      const correctIndex = num - 1;
      const distance = math.abs(index - correctIndex);
      if (distance > 0 && distance < minDistance) {
        minDistance = distance;
        closestNumber = { num, currentIndex: index, correctIndex };
      }
    });

    return closestNumber;
  };

  const handleHint = () => {
    if (gameOver) return;
    buttonClickSound.current.play();
    const nextMove = getNextHintNumber();
    if (nextMove) {
      setHintNumber(nextMove.num);
      setHintPosition(nextMove.correctIndex);
    }
  };

  const resetHint = () => {
    setHintLevel(0);
    setHintNumber(null);
    setHintPosition(null);
  };

  const findPathToPosition = (start, end, emptyPos) => {
    // Simple BFS to find a path from start to end
    const queue = [[emptyPos]];
    const visited = new Set([emptyPos]);

    while (queue.length > 0) {
      const path = queue.shift();
      const current = path[path.length - 1];

      if (current === start) {
        return path;
      }

      // Get possible moves
      const possibleMoves = [];
      if (current >= 4) possibleMoves.push(current - 4); // up
      if (current < 12) possibleMoves.push(current + 4); // down
      if (current % 4 !== 0) possibleMoves.push(current - 1); // left
      if (current % 4 !== 3) possibleMoves.push(current + 1); // right

      for (const move of possibleMoves) {
        if (!visited.has(move)) {
          visited.add(move);
          queue.push([...path, move]);
        }
      }
    }

    return [];
  };

  // Reset hint when board changes
  useEffect(() => {
    resetHint();
  }, [board]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getHintMessage = (hintLevel, number) => {
    if (!number) return "";
    return `Focus on moving number ${number}`;
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleHowToModal = () => setShowHowToModal((prev) => !prev);

  // Initialize board on first mount
  useEffect(() => {
    initializeBoard();
  }, []);

  // Add effect for keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Check for Ctrl/Cmd + Shift + A
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "a"
      ) {
        e.preventDefault();
        if (!gameOver) {
          // Auto solve the board
          const solvedBoard = Array.from({ length: 15 }, (_, i) => i + 1);
          solvedBoard.push(null); // Add empty space at the end
          setBoard(solvedBoard);
          setHasStarted(true);
          setElapsedTime(1); // Set a minimal time
          updateCorrectTiles(solvedBoard);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [gameOver]);

  return (
    <GameContainer>
      <GameCard>
        <CardContent sx={{ padding: 0 }}>
          <Paper
            elevation={2}
            sx={{
              width: "100%",
              maxWidth: "sm",
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: "white",
            }}
          >
            {/* Top row: Controls, Date, Streak */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              {/* Left: Help and Sound controls */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="How to Play">
                  <IconButton
                    onClick={() => {
                      buttonClickSound.current.play();
                      toggleHowToModal();
                    }}
                    sx={{
                      color: "#222",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    }}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                  <IconButton
                    onClick={() => {
                      buttonClickSound.current.play();
                      toggleMute();
                    }}
                    sx={{
                      color: "#222",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    }}
                  >
                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Center: Date */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  color: "#222",
                  cursor: "pointer",
                  textDecoration: "underline",
                  transition: "text-decoration 0.2s",
                  "&:hover": { textDecoration: "underline dotted" },
                }}
                onClick={() => {
                  buttonClickSound.current.play();
                  setShowCalendar(true);
                }}
              >
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </Typography>

              {/* Right: Streak */}
              <Tooltip title="Keep playing daily to unlock special features!">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StreakCounter isActive={isStreakActive} streak={streak} />
                </Box>
              </Tooltip>
            </Box>

            {/* Bottom row: Timer and Best Time */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left: Timer */}
              <Chip
                icon={<TimerIcon sx={{ color: "#FFD700" }} />}
                label={`Time: ${formatTime(elapsedTime)}`}
                sx={{
                  bgcolor: "rgba(0, 0, 0, 0.1)",
                  "& .MuiChip-label": {
                    fontWeight: 600,
                    color: "#000000",
                  },
                }}
              />

              {/* Right: Best Time */}
              <Chip
                icon={<EmojiEventsIcon sx={{ color: "#FFD700" }} />}
                label={`Best: ${
                  bestTime !== null ? formatTime(bestTime) : "---"
                }`}
                sx={{
                  bgcolor: "rgba(0, 0, 0, 0.1)",
                  "& .MuiChip-label": {
                    fontWeight: 600,
                    color: "#222",
                  },
                  animation: newBestTime ? "pulse 1s ease-in-out" : "none",
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.05)" },
                    "100%": { transform: "scale(1)" },
                  },
                }}
              />
            </Box>
          </Paper>

          <GameGrid>
            {board.map((number, index) => (
              <GameTile
                key={index}
                onClick={() => handleTileClick(index)}
                disabled={gameOver || number === null}
                isCorrect={correctTiles.has(index)}
                isEmpty={number === null}
              >
                {number}
              </GameTile>
            ))}
          </GameGrid>
        </CardContent>
      </GameCard>

      {/* Calendar Archive Modal */}
      <CalendarArchive
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        gameKey="digitshift"
        getGameDataForDate={(dateKey) => {
          const data = localStorage.getItem(`digitshift-${dateKey}`);
          if (!data) return null;
          const gameData = JSON.parse(data);
          return {
            completed: gameData.completed,
            time: gameData.time,
            best: gameData.bestTime ? formatTime(gameData.bestTime) : null,
          };
        }}
        formatDateForStorage={formatDateForStorage}
        title="DigitShift Archive"
        description="View your past DigitShift games and achievements"
        onDateSelect={handleDateSelect}
        currentDate={currentDate}
        showGoal={false}
      />

      {/* End Game Modal */}
      <EndGameModal
        open={showEndModal}
        onClose={() => {
          buttonClickSound.current.play();
          setShowEndModal(false);
        }}
        onPlayAgain={() => {
          buttonClickSound.current.play();
          initializeBoard();
          setGameOver(false);
          setNewBestTime(false);
          setShowEndModal(false);
        }}
        score={elapsedTime}
        bestScore={bestTime}
        newBestScore={newBestTime}
        gameType="digitshift"
        timeMode={true}
      />

      {/* How To Play Modal */}
      <HowToPlayModal
        open={showHowToModal}
        onClose={toggleHowToModal}
        gameType="digitshift"
      />
    </GameContainer>
  );
};

export default DigitShift;
